import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Check } from "lucide-react";
import PublicLayout, { H1, SectionCta } from "../../components/PublicLayout";
import { SITE } from "../../content/site";
import { micro } from "../../lib/microcopy";
import { routePath, SITE_URL } from "../../lib/routes";
import { META } from "../../lib/seo-meta";
import { orgRef } from "../../components/Seo";
import { track, EVENTS } from "../../lib/analytics";

const VISUALS = {
  productArena: "/assets/products/arena.webp",
  productCentauros: "/assets/products/centauros.webp",
  productAos: "/assets/products/aos.webp",
};

export default function ProductDetail({ lang, productKey }) {
  const p = SITE[lang].productPages[productKey];
  const c = SITE[lang];
  const dev = (SITE[lang].home2 || SITE.en.home2).developments.find((d) => d.route === productKey);
  const [openFaq, setOpenFaq] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); track(EVENTS.PRODUCT_VIEWED, { product: p.name, from: "product_page" }); }, [lang, productKey, p.name]);

  const ld = [
    { "@context": "https://schema.org", "@type": "Service",
      name: p.name, serviceType: "Equestrian software platform",
      url: `${SITE_URL}${routePath(productKey, lang)}`,
      description: p.what, provider: orgRef(), brand: orgRef(),
      image: `${SITE_URL}${META[productKey][lang].image}`, inLanguage: lang },
    { "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: p.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  ];

  return (
    <PublicLayout pageKey={productKey} lang={lang} jsonLd={ld}
      crumbs={[{ name: c.breadcrumbs.products, path: routePath("products", lang) }, { name: p.name, path: routePath(productKey, lang) }]}>
      <div className="mx-auto max-w-[900px] px-6 py-14 md:px-8 md:py-20" data-testid={`product-page-${productKey}`}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0e7c6b]">{p.status}</p>
        <div className="mt-4"><H1>{p.h1}</H1></div>

        <figure className="mt-10 overflow-hidden rounded-[2rem] border border-black/8 bg-white" data-testid="product-visual">
          <img src={VISUALS[productKey]} alt={`${p.name} — ${c.breadcrumbs.products}`} width="1536" height="1024"
            className="mx-auto max-h-[520px] w-full object-contain p-4 md:p-8" />
          {dev && (
            <figcaption className="border-t border-black/6 px-6 pb-6 pt-5 text-center" data-testid="product-visual-caption">
              <p className="text-sm font-light leading-snug text-[#5a5d58] md:text-base">{dev.tag}</p>
              <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#050505]">{p.name}</p>
            </figcaption>
          )}
        </figure>

        <p className="mt-10 text-lg font-light leading-relaxed text-[#2a2b29]">{p.what}</p>
        <p className="mt-5 text-base font-light leading-relaxed text-[#6b6f6c]">{p.audience}</p>

        <section className="mt-14 border-t border-black/8 pt-10">
          <h2 className="font-display text-2xl font-normal text-[#050505]">{micro(lang, "whatItDoes")}</h2>
          <ul className="mt-6 space-y-3">
            {p.capabilities.map((cap, i) => (
              <li key={i} className="flex items-start gap-3 text-base font-light leading-relaxed text-[#2a2b29]">
                <Check size={17} className="mt-1 shrink-0 text-[#0e7c6b]" /> {cap}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14 border-t border-black/8 pt-10" data-testid="product-faq">
          <h2 className="font-display text-2xl font-normal text-[#050505]">FAQ</h2>
          <div className="mt-4 divide-y divide-black/8 border-y border-black/8">
            {p.faq.map((f, i) => (
              <div key={i}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}
                  className="flex w-full items-center justify-between gap-4 py-5 text-left" data-testid={`product-faq-${i}`}>
                  <span className="text-base font-normal text-[#050505]">{f.q}</span>
                  <ChevronDown size={18} className={`shrink-0 text-[#9a9d98] transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="pb-5 text-base font-light leading-relaxed text-[#6b6f6c]">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>

        <p className="mt-10 text-sm font-light text-[#9a9d98]">
          {micro(lang, "productBy")}
          <Link to={routePath("about", lang)} className="font-medium text-[#0e7c6b] hover:underline">Equestrian Partners</Link>
        </p>
        <SectionCta title={micro(lang, "interestedIn", { name: p.name })}
          text={micro(lang, "productCtaText")}
          lang={lang} onClick={() => track(EVENTS.CTA_CLICK, { cta: `product_${p.name}_contact` })} />
      </div>
    </PublicLayout>
  );
}
